import { useEffect, useRef } from 'react';

type AnyEvent = MouseEvent | TouchEvent;

export const useClickOutside = <T extends HTMLElement>(handler: (event: AnyEvent) => void) => {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const el = ref.current;
      if (!el || el.contains(event.target as Node)) {
        return;
      }

      // Ignore clicks inside a modal portal only when the watched element lives
      // outside that modal (e.g. a page-level dropdown the modal opened over).
      // If el is inside the same portal, this is a real outside-click and must fire.
      const target = event.target as Element | null;
      const portal = target?.closest('[data-modal-portal]');
      if (portal && !portal.contains(el)) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [handler]);

  return ref;
};
